import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DateTime } from "luxon";
import { FileArchive, FileText, Image, File } from "lucide-react";

export interface Person {
  givenName?: string | null | undefined;
  familyName?: string | null | undefined;
  emailAddress?: string | null | undefined;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function truncateMiddle(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;

  const half = maxLength / 2;
  return `${text.slice(0, half)}...${text.slice(-half)}`;
}

export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

export function getInitialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function getInitials(person?: Person): string {
  if (person) {
    const firstInitial = person.givenName?.[0]?.toUpperCase() ?? "";
    const lastInitial = person.familyName?.[0]?.toUpperCase() ?? "";

    if (firstInitial || lastInitial) {
      return firstInitial + lastInitial;
    }

    if (person.emailAddress) {
      const emailParts = person.emailAddress.split("@")[0];
      const parts = emailParts.split(".");

      if (parts.length === 1) {
        // no dots in email, use first letter
        return parts[0][0]?.toUpperCase() ?? "";
      } else {
        // has dots, collect first letters if they are letters
        return parts.reduce((initials, part) => {
          const firstChar = part[0];
          if (/[a-zA-Z]/.exec(firstChar)) {
            return initials + firstChar.toUpperCase();
          }

          return initials;
        }, "");
      }
    }
  }

  return "";
}

export function formatTime(date: string): string {
  const localDate = DateTime.fromISO(date, { setZone: true }).toLocal();
  return localDate.toFormat("HH:mm 'Uhr'");
}

export function formatDateTimeRelative(date?: string | null): string {
  if (!date) {
    return "";
  }

  const localDate = DateTime.fromISO(date, { setZone: true }).toLocal();
  const time = localDate.toFormat("HH:mm 'Uhr'");
  const now = DateTime.local();

  if (localDate.hasSame(now, "day")) {
    return time;
  } else if (localDate.hasSame(now.minus({ days: 1 }), "day")) {
    return `Gestern, ${time}`;
  } else if (localDate >= now.minus({ days: 7 })) {
    const dayName = localDate.setLocale(navigator.language).toFormat("EEEE");
    return `${dayName}, ${time}`;
  } else {
    const dateStr = localDate.toFormat("dd.MM.yyyy");
    return `${dateStr}, ${time}`;
  }
}

export function formatDateTime(
  date?: string | null,
  showSeconds = false,
): string {
  if (!date) {
    return "";
  }

  const localDate = DateTime.fromISO(date, { setZone: true }).toLocal();
  const format = showSeconds
    ? "dd.MM.yyyy, HH:mm:ss 'Uhr'"
    : "dd.MM.yyyy, HH:mm 'Uhr'";
  return localDate.toFormat(format);
}

export function formatRelativeDate(date?: string | null): string {
  if (!date) return "";
  const dt = DateTime.fromISO(date, { setZone: true }).toLocal();
  return dt.toRelative({ locale: "de" }) ?? formatDateTime(date);
}

/**
 * Compact relative time for dense lists: `justNow` under a minute ("gerade eben"), then
 * luxon's short German form ("vor 5 Min.", "vor 2 Std."). `formatRelativeDate` is not
 * enough there: it prints the same "vor 3 Stunden" in full width for everything.
 */
export function formatRelativeDateShort(
  date: string | null | undefined,
  justNow: string,
): string {
  if (!date) return "";
  const dt = DateTime.fromISO(date, { setZone: true }).toLocal();
  // Under a minute, and also a stamp a little in the future (clock skew), reads as "now".
  if (DateTime.local().diff(dt, "seconds").seconds < 60) return justNow;
  return (
    dt.toRelative({ locale: "de", style: "short" }) ?? formatDateTime(date)
  );
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

export function looksLikeIsoDate(value: string): boolean {
  return ISO_DATE_REGEX.test(value);
}

export function getAttachmentIcon(contentType: string) {
  if (contentType.startsWith("image/")) {
    return <Image className="w-4 h-4" />;
  }
  if (
    contentType.startsWith("text/") ||
    contentType.startsWith("application/pdf")
  ) {
    return <FileText className="w-4 h-4" />;
  }
  if (contentType.includes("zip") || contentType.includes("compressed")) {
    return <FileArchive className="w-4 h-4" />;
  }
  return <File className="w-4 h-4" />;
}
